
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Job, JobStatus, Lead, Tree, User } from '../types';
import CustomerIntakeForm from './CustomerIntakeForm';
import SitePlanner from './SitePlanner';
import ArboristChat from './ArboristChat';
import EducationView from './EducationView';
import { VaultView } from './VaultView';
import { Icons } from '../constants';
import { Logo, BrandName } from './Logo';

// Three.js is only needed when a customer opens the 3D survey tab.
const PropertySurvey3D = lazy(() => import('./PropertySurvey3D'));

interface Props {
  jobs: Job[];
  onUpdate: (job: Job) => void;
  onAddLead: (lead: Lead) => void;
  user?: User;
  onNavigate?: (tab: string) => void;
  initialTab?: 'overview' | 'planner' | 'advisor' | 'remote' | 'education' | 'vault';
}

const ClientPortalView: React.FC<Props> = ({ jobs, onUpdate, onAddLead, user, onNavigate, initialTab = 'overview' }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'planner' | 'advisor' | 'remote' | 'education' | 'vault'>(initialTab);

  // The dashboard links to this view with different initial tabs (Vault,
  // Education). React reuses the component between those routes, so the
  // initial state alone would leave the previous tab selected.
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  
  const activeJob = jobs[0];

  const handleUpdateInventory = (inventory: Tree[]) => {
    if (activeJob) {
      onUpdate({ ...activeJob, inventory });
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-fade-in">
      {/* Brand Header */}
      <div className="bg-bark p-12 rounded-[48px] text-white flex flex-col md:flex-row justify-between items-center relative overflow-hidden shadow-2xl">
        <div className="relative z-10 text-center md:text-left">
          <div className="flex items-center space-x-3 mb-4 justify-center md:justify-start">
            <Logo className="w-12 h-12" />
            <BrandName className="text-2xl text-gold" />
          </div>
          <h2 className="text-4xl font-black mb-2 leading-tight">Your Property Arbo-Vault</h2>
          <p className="text-stone-400 font-bold text-lg italic tracking-tight mb-8">Professional Tree Care.</p>
          <div className="flex flex-wrap justify-center md:justify-start gap-4">
            <button 
              onClick={() => {
                if (user?.role === 'guest') {
                  onNavigate?.('guest-login-prompt');
                } else {
                  onNavigate?.('client-catalog');
                }
              }}
              className="px-8 py-4 bg-gold text-bark font-black rounded-2xl hover:bg-white transition shadow-xl shadow-gold/10 uppercase tracking-widest text-sm"
            >
              Request Service
            </button>
          </div>
        </div>
        
        {/* QR Share Widget (Google Review QR) */}
        <div className="relative z-10 mt-8 md:mt-0 bg-white p-4 rounded-[32px] shadow-2xl border-4 border-stone-200/50 group hover:scale-105 transition duration-500">
           <div className="bg-stone-50 rounded-[24px] p-2 border-2 border-stone-100 overflow-hidden">
              <img 
                src="/qr.png" 
                alt="Google Review QR Code" 
                className="w-32 h-32 object-contain"
                onError={(e) => {
                  e.currentTarget.src = "https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://g.page/review/greentree";
                }}
              />
           </div>
           <p className="text-[10px] font-black text-bark uppercase text-center mt-3 tracking-widest">Review Us on Google</p>
        </div>

        <div className="absolute right-0 bottom-0 opacity-5 transform translate-x-20 translate-y-20 scale-150 pointer-events-none text-gold">
           <div className="w-96 h-96 border-[40px] border-current rounded-full" />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 bg-white p-2 rounded-[32px] border border-stone-200 shadow-sm w-fit mx-auto md:mx-0">
        <button 
          onClick={() => setActiveTab('overview')}
          className={`px-6 py-3 rounded-[24px] font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'overview' ? 'bg-bark text-gold shadow-lg' : 'text-stone-400 hover:bg-stone-50'}`}
        >
          My Portfolio
        </button>
        <button 
          onClick={() => setActiveTab('planner')}
          className={`px-6 py-3 rounded-[24px] font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'planner' ? 'bg-bark text-gold shadow-lg' : 'text-stone-400 hover:bg-stone-50'}`}
        >
          Property Map
        </button>
        <button 
          onClick={() => setActiveTab('advisor')}
          className={`px-6 py-3 rounded-[24px] font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'advisor' ? 'bg-bark text-gold shadow-lg' : 'text-stone-400 hover:bg-stone-50'}`}
        >
          Talk to Arb-E
        </button>
        <button 
          onClick={() => setActiveTab('remote')}
          className={`px-6 py-3 rounded-[24px] font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'remote' ? 'bg-bark text-gold shadow-lg' : 'text-stone-400 hover:bg-stone-50'}`}
        >
          Remote Consulting
        </button>
        <button 
          onClick={() => setActiveTab('education')}
          className={`px-6 py-3 rounded-[24px] font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'education' ? 'bg-bark text-gold shadow-lg' : 'text-stone-400 hover:bg-stone-50'}`}
        >
          Education
        </button>
        <button 
          onClick={() => setActiveTab('vault')}
          className={`px-6 py-3 rounded-[24px] font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'vault' ? 'bg-bark text-gold shadow-lg' : 'text-stone-400 hover:bg-stone-50'}`}
        >
          Vault
        </button>
      </div>

      <div className="transition-all duration-500">
        {activeTab === 'overview' && (
          <div className="grid gap-8">
            {jobs.map(job => (
              <div key={job.id} className="bg-white rounded-[40px] border border-stone-200 p-10 shadow-sm hover:shadow-xl transition-shadow group relative overflow-hidden">
                <div className="absolute top-0 right-0 p-10 opacity-5 text-bark group-hover:scale-110 transition-transform">
                   <Icons.Safety />
                </div>
                <div className="flex flex-col md:flex-row justify-between items-start mb-10 gap-6 relative z-10">
                  <div>
                    <span className="text-[10px] font-black text-gold bg-gold/5 px-3 py-1 rounded-full uppercase tracking-[0.2em] border border-gold/10">{job.status === JobStatus.ESTIMATING ? 'Proposal Ready' : 'Status: ' + job.status}</span>
                    <h3 className="text-3xl font-black text-bark mt-4 tracking-tight">{job.serviceType} Project</h3>
                    <p className="text-stone-400 font-bold text-sm mt-2">{job.address}</p>
                  </div>
                  <div className="text-right bg-stone-50 p-6 rounded-[32px] border border-stone-100">
                    <p className="text-[10px] text-stone-400 uppercase font-black tracking-widest mb-1">Total Investment</p>
                    <p className="text-4xl font-black text-palm tracking-tighter">${job.totalPrice.toLocaleString()}</p>
                  </div>
                </div>

                <div className="space-y-4 mb-10">
                  <p className="text-[10px] font-black text-stone-300 uppercase tracking-widest pb-4 border-b border-stone-100">Scope of Work</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {job.lineItems.map(item => (
                      <div key={item.id} className="flex justify-between items-center p-6 bg-stone-50 rounded-[28px] border border-stone-200 group-hover:bg-white transition-colors">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 rounded-2xl bg-gold/10 flex items-center justify-center text-gold">
                            <Icons.Tree />
                          </div>
                          <span className="font-black text-bark text-sm uppercase tracking-tight">{item.description}</span>
                        </div>
                        <span className="font-black text-palm">${(item.unitPrice * item.quantity).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {job.attachments && job.attachments.length > 0 && (
                  <div className="space-y-4 mb-10">
                    <p className="text-[10px] font-black text-stone-300 uppercase tracking-widest pb-4 border-b border-stone-100">Attachments & Diagrams</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {job.attachments.map((src, idx) => (
                        <div key={idx} className="relative group rounded-2xl overflow-hidden border border-stone-200">
                          <img src={src} alt={`Attachment ${idx + 1}`} className="w-full h-32 object-cover" />
                          <a 
                            href={src} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="absolute inset-0 bg-bark/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <span className="text-white font-bold text-sm">View Full</span>
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4">
                  {job.status === JobStatus.ESTIMATING && (
                    <>
                      <button 
                        onClick={() => onUpdate({...job, status: JobStatus.SCHEDULED})}
                        className="flex-1 py-5 bg-gecko text-white rounded-3xl font-black text-sm uppercase tracking-[0.2em] hover:bg-palm transition transform active:scale-95 shadow-2xl shadow-gecko/20"
                      >
                        Approve Proposal
                      </button>
                      <button 
                        onClick={() => setActiveTab('advisor')}
                        className="px-10 py-5 border-2 border-stone-200 rounded-3xl font-black text-sm uppercase tracking-widest text-stone-500 hover:bg-stone-50 transition"
                      >
                        Ask Arb-E
                      </button>
                    </>
                  )}
                  {job.status === JobStatus.INVOICED && (
                    <button 
                      onClick={() => onUpdate({...job, status: JobStatus.PAID})}
                      className="w-full py-5 bg-palm text-white rounded-3xl font-black text-sm uppercase tracking-[0.2em] shadow-2xl shadow-palm/20 transition-all hover:scale-[1.01]"
                    >
                      Complete Payment →
                    </button>
                  )}
                  {job.status === JobStatus.PAID && (
                    <div className="w-full p-6 bg-leaf/20 rounded-3xl flex items-center justify-center text-palm font-black border border-palm/10 text-sm uppercase tracking-widest">
                       Project Successfully Finalized
                    </div>
                  )}
                </div>
              </div>
            ))}
            {jobs.length === 0 && (
              <div className="text-center py-32 bg-white rounded-[48px] border-2 border-dashed border-stone-200 flex flex-col items-center">
                <div className="w-20 h-20 bg-stone-50 rounded-3xl flex items-center justify-center mb-6 text-stone-300 transform rotate-12">
                  <Icons.Tree />
                </div>
                <p className="text-stone-400 font-black text-xl tracking-tight uppercase">No active projects found.</p>
                <button 
                  onClick={() => onNavigate?.('client-catalog')}
                  className="mt-8 bg-bark text-gold px-10 py-4 rounded-2xl font-black uppercase tracking-widest text-sm hover:scale-105 transition shadow-xl"
                >
                  Request Consultation
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'planner' && (
          <div className="bg-white rounded-[48px] border border-stone-200 p-10 shadow-sm">
            <div className="space-y-8">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.16em] text-stone-400 mb-3">2D field plot</p>
                <SitePlanner
                  inventory={activeJob?.inventory || []}
                  onUpdateInventory={handleUpdateInventory}
                />
              </div>
              <div className="pt-8 border-t border-stone-100">
                <Suspense fallback={<div className="h-[420px] rounded-3xl bg-stone-100 animate-pulse" />}>
                  <PropertySurvey3D
                    address={activeJob?.address}
                    trees={activeJob?.inventory || []}
                    onUpdateTrees={handleUpdateInventory}
                  />
                </Suspense>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'advisor' && (
          <div className="max-w-4xl mx-auto">
            <ArboristChat address={activeJob?.address} />
          </div>
        )}

        {activeTab === 'remote' && (
          <div className="bg-white rounded-[48px] border border-stone-200 p-10 shadow-sm">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-black text-bark mb-4">Remote Consulting Workflow</h3>
              <p className="text-stone-500 max-w-2xl mx-auto">
                Get expert arboriculture advice without an on-site visit. We use HD video conferencing, secure file sharing, and aerial drone flights to assess your property and provide actionable recommendations.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6 relative">
              {/* Connecting line */}
              <div className="hidden md:block absolute top-1/2 left-0 w-full h-1 bg-stone-100 -translate-y-1/2 z-0"></div>

              {/* Step 1 */}
              <div className="relative z-10 bg-white p-6 rounded-3xl border-2 border-stone-100 text-center shadow-sm hover:border-gold transition-colors">
                <div className="w-16 h-16 bg-gold/10 text-gold rounded-full flex items-center justify-center mx-auto mb-4 font-black text-xl">1</div>
                <h4 className="font-black text-bark mb-2 uppercase tracking-widest text-sm">Upload Media</h4>
                <p className="text-xs text-stone-500">Share photos, videos, or property documents securely through your portal.</p>
                <button className="mt-4 w-full py-2 bg-stone-100 text-stone-600 rounded-xl text-xs font-bold hover:bg-stone-200 transition">Upload Files</button>
              </div>

              {/* Step 2 */}
              <div className="relative z-10 bg-white p-6 rounded-3xl border-2 border-stone-100 text-center shadow-sm hover:border-gecko transition-colors">
                <div className="w-16 h-16 bg-gecko/10 text-gecko rounded-full flex items-center justify-center mx-auto mb-4 font-black text-xl">2</div>
                <h4 className="font-black text-bark mb-2 uppercase tracking-widest text-sm">Drone Flight</h4>
                <p className="text-xs text-stone-500">We dispatch a drone to capture high-resolution aerial imagery of your canopy.</p>
                <button 
                  onClick={() => onNavigate?.('client-drones')}
                  className="mt-4 w-full py-2 bg-stone-100 text-stone-600 rounded-xl text-xs font-bold hover:bg-stone-200 transition"
                >
                  Schedule Drone
                </button>
              </div>

              {/* Step 3 */}
              <div className="relative z-10 bg-white p-6 rounded-3xl border-2 border-stone-100 text-center shadow-sm hover:border-palm transition-colors">
                <div className="w-16 h-16 bg-palm/10 text-palm rounded-full flex items-center justify-center mx-auto mb-4 font-black text-xl">3</div>
                <h4 className="font-black text-bark mb-2 uppercase tracking-widest text-sm">Video Consult</h4>
                <p className="text-xs text-stone-500">Meet with our ISA Certified Arborist via HD video to review findings.</p>
                <button className="mt-4 w-full py-2 bg-stone-100 text-stone-600 rounded-xl text-xs font-bold hover:bg-stone-200 transition">Book Meeting</button>
              </div>

              {/* Step 4 */}
              <div className="relative z-10 bg-white p-6 rounded-3xl border-2 border-stone-100 text-center shadow-sm hover:border-bark transition-colors">
                <div className="w-16 h-16 bg-bark/10 text-bark rounded-full flex items-center justify-center mx-auto mb-4 font-black text-xl">4</div>
                <h4 className="font-black text-bark mb-2 uppercase tracking-widest text-sm">Action & Payment</h4>
                <p className="text-xs text-stone-500">Receive your detailed report and complete payment for the consultation.</p>
                <button className="mt-4 w-full py-2 bg-palm text-white rounded-xl text-xs font-bold hover:bg-bark transition">Pay Invoice</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'education' && (
          <EducationView />
        )}

        {activeTab === 'vault' && (
          <VaultView />
        )}
      </div>
    </div>
  );
};

export default ClientPortalView;
